import React, { useEffect, useState } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface Pack {
  id: string;
  name: string;
  description: string | null;
  captainId: string | null;
  duesStatus: string;
  duesLastPaid: string | null;
  duesAmount: string | null;
  duesDueDate: string | null;
  duesNotes: string | null;
  archived: boolean;
  memberCount: number;
  captain: { id: string; displayName: string; email: string } | null;
  pendingInvites: Array<{ id: string; code: string; inviteeEmail: string | null; role: string | null; expiresAt: string }>;
}

interface Camp { id: string; name: string; }
interface AlmanacYear { id: string; year: number; totalWeeks: number; firstWeekStart: string; bonusWeek: number | null; }
interface CampClosure { campId: string; almanacYearId: string; weeks: number[]; }

// Ontario-based week names (same as AlmanacPage)
const WEEK_NAMES: Record<number, string> = {
  1: 'New Year / Ice Fishing Opens', 2: 'Ice Fishing', 3: 'Ice Fishing', 4: 'Late Ice / Coyote',
  5: 'February Ice', 6: 'Mid-Winter Ice', 7: 'Late Winter Ice', 8: 'Late Winter / Rabbit',
  9: 'March Break Prep', 10: 'March Break', 11: 'Late Ice / Thaw Watch', 12: 'Ice Out Watch',
  13: 'Spring Thaw / Prep', 14: 'Early Spring / Camp Opening', 15: 'Spring Turkey Opener',
  16: 'Spring Turkey', 17: 'Turkey / Walleye Opener', 18: 'Walleye Season',
  19: 'Late Spring Fishing', 20: 'Victoria Day / Fishing', 21: 'Spring Fishing Peak',
  22: 'Bass Pre-Season', 23: 'Early Summer / Trout', 24: 'Bass Opener Week',
  25: 'Bass Season', 26: 'Canada Day / Bass', 27: 'Summer Fishing', 28: 'Summer Peak',
  29: 'Mid-Summer', 30: 'Summer / Swimming', 31: 'August Fishing', 32: 'Civic Holiday',
  33: 'Late Summer Fishing', 34: 'Summer Wind-Down', 35: 'Labour Day Prep',
  36: 'Labour Day / Fall Prep', 37: 'Early Bear Season', 38: 'Bear / Grouse',
  39: 'Fall Colours / Grouse', 40: 'Thanksgiving / Waterfowl', 41: 'Waterfowl Season',
  42: 'Deer Bow Opener', 43: 'Deer Bow / Waterfowl', 44: 'Moose Opener (Gun)',
  45: 'Moose Week', 46: 'Deer Rifle Opener', 47: 'Deer Rifle Season',
  48: 'Late Deer Rifle', 49: 'Muzzleloader / Late Deer', 50: 'Winter Prep / Closing',
  51: 'Camp Winterization', 52: 'Year End / Closed', 53: 'Bonus / Holiday Week',
};

function getWeekDate(firstWeekStart: string, weekNum: number): string {
  const d = new Date(firstWeekStart);
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

type PanelMode =
  | { type: 'default' }
  | { type: 'create' }
  | { type: 'edit'; pack: Pack }
  | { type: 'view'; pack: Pack }
  | { type: 'created'; pack: any; code: string; email: string; emailSent: boolean };

function DuesBadge({ status }: { status: string }) {
  const colors: Record<string, string> = { paid: '#28A745', pending: '#FFC107', overdue: '#DC3545', grace: '#17A2B8' };
  return <span style={{ ...st.badge, backgroundColor: `${colors[status] || '#888'}22`, color: colors[status] || '#888' }}>{status}</span>;
}

export function PacksPage() {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [almanacYears, setAlmanacYears] = useState<AlmanacYear[]>([]);
  const [closures, setClosures] = useState<CampClosure[]>([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<PanelMode>({ type: 'default' });

  // Create form
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formCampId, setFormCampId] = useState('');
  const [formYear, setFormYear] = useState(0);
  const [formWeek, setFormWeek] = useState(0);

  // Edit form
  const [editName, setEditName] = useState('');
  const [editDuesStatus, setEditDuesStatus] = useState('pending');
  const [editDuesAmount, setEditDuesAmount] = useState('');
  const [editDuesNotes, setEditDuesNotes] = useState('');
  const [editArchived, setEditArchived] = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [packsRes, campsRes, almanacRes] = await Promise.all([
      apiFetch<Pack[]>('/api/admin/packs'),
      apiFetch<Camp[]>('/api/admin/camps'),
      apiFetch<{ years: AlmanacYear[] }>('/api/admin/almanac'),
    ]);
    if (packsRes.success && packsRes.data) setPacks(packsRes.data);
    if (campsRes.success && campsRes.data) setCamps(campsRes.data);
    if (almanacRes.success && almanacRes.data?.years) setAlmanacYears(almanacRes.data.years);
    if (almanacRes.success && almanacRes.data?.closures) setClosures(almanacRes.data.closures);
    setLoading(false);
  }

  function openCreate() {
    setFormName('');
    setFormDesc('');
    setFormEmail('');
    setFormCampId(camps[0]?.id || '');
    setFormYear(almanacYears[0]?.year || 0);
    setFormWeek(0);
    setPanel({ type: 'create' });
  }

  async function createPack() {
    if (!formName.trim() || !formEmail.trim()) return;
    const res = await apiFetch<{ pack: any; inviteCode: string; stewardEmail: string }>('/api/admin/packs', {
      method: 'POST',
      body: JSON.stringify({
        name: formName,
        description: formDesc || undefined,
        steward_email: formEmail,
        camp_id: formCampId || undefined,
        almanac_year: formYear || undefined,
        almanac_week: formWeek || undefined,
      }),
    });
    if (res.success && res.data) {
      setPanel({ type: 'created', pack: res.data.pack, code: res.data.inviteCode, email: res.data.stewardEmail, emailSent: res.data.emailSent ?? false });
      loadAll();
    }
  }

  function openView(pack: Pack) { setPanel({ type: 'view', pack }); }

  function openEdit(pack: Pack) {
    setEditName(pack.name);
    setEditDuesStatus(pack.duesStatus);
    setEditDuesAmount(pack.duesAmount || '');
    setEditDuesNotes(pack.duesNotes || '');
    setEditArchived(pack.archived);
    setPanel({ type: 'edit', pack });
  }

  async function savePack() {
    if (panel.type !== 'edit') return;
    await apiFetch(`/api/admin/packs/${panel.pack.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: editName,
        dues_status: editDuesStatus,
        dues_amount: editDuesAmount ? parseFloat(editDuesAmount) : undefined,
        dues_notes: editDuesNotes || undefined,
        archived: editArchived,
      }),
    });
    setPanel({ type: 'default' });
    loadAll();
  }

  async function deletePack(pack: Pack) {
    if (!confirm(`DELETE pack "${pack.name}"? This will permanently remove the pack, all members, stays, and invite codes. This cannot be undone.`)) return;
    await apiFetch(`/api/admin/packs/${pack.id}`, { method: 'DELETE' });
    setPanel({ type: 'default' });
    loadAll();
  }

  async function sendInvite(packId: string) {
    const email = prompt('Enter email address for invite:');
    if (!email) return;
    const res = await apiFetch<{ code: string }>(`/api/admin/packs/${packId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role: 'member' }),
    });
    if (res.success && res.data) {
      alert(`Invite code: ${res.data.code}\nSend to: ${email}`);
      loadAll();
    }
  }

  if (loading) return <div style={st.loading}>Loading packs...</div>;

  return (
    <div style={st.page}>
      {/* LEFT */}
      <div style={st.leftCol}>
        <div style={st.header}>
          <div>
            <h1 style={st.pageTitle}>Packs</h1>
            <p style={st.pageSubtitle}>Manage packs, stewards, and invitations</p>
          </div>
          <button style={st.btnPrimary} onClick={openCreate}>+ Create Pack</button>
        </div>

        {packs.length === 0 ? (
          <p style={st.empty}>No packs yet. Create your first pack to get started.</p>
        ) : (
          <div style={st.card}>
            <table>
              <thead>
                <tr>
                  <th>Pack</th>
                  <th>Steward</th>
                  <th>Members</th>
                  <th>Dues</th>
                  <th>Invites</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {packs.map((pack) => (
                  <tr key={pack.id} style={pack.archived ? { opacity: 0.5 } : {}}>
                    <td>
                      <div style={{ color: COLORS.textPrimary, fontWeight: 500, cursor: 'pointer' }} onClick={() => openView(pack)}>
                        {pack.name}
                      </div>
                      {pack.archived && <span style={{ ...st.badge, backgroundColor: 'rgba(136,136,136,0.2)', color: '#888' }}>archived</span>}
                    </td>
                    <td style={{ color: COLORS.textSecondary, fontSize: 12 }}>{pack.captain?.displayName || <em style={{ color: COLORS.textMuted }}>pending invite</em>}</td>
                    <td>{pack.memberCount}</td>
                    <td><DuesBadge status={pack.duesStatus} /></td>
                    <td>
                      {pack.pendingInvites?.length > 0 ? (
                        <span style={{ ...st.badge, backgroundColor: 'rgba(255,193,7,0.15)', color: '#FFC107' }}>
                          {pack.pendingInvites.length} pending
                        </span>
                      ) : '--'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={st.btnSmallMuted} onClick={() => openEdit(pack)}>Edit</button>
                        <button style={st.btnSmall} onClick={() => sendInvite(pack.id)}>Invite</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* RIGHT */}
      <div style={st.panelCol}>
        <div style={st.panelContent}>
          {panel.type === 'default' && (
            <>
              <h3 style={st.panelTitle}>Packs</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 13 }}>
                {packs.length} pack{packs.length !== 1 ? 's' : ''} registered.
                Create a pack and invite a steward to manage it.
              </p>
              <button style={{ ...st.btnPrimary, width: '100%', marginTop: 16 }} onClick={openCreate}>+ Create Pack</button>
            </>
          )}

          {panel.type === 'create' && (
            <>
              <h3 style={st.panelTitle}>Create Pack</h3>
              <div style={st.field}>
                <label style={st.label}>Pack Name</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} style={st.input} autoFocus placeholder="e.g. Alpha Pack" />
              </div>
              <div style={st.field}>
                <label style={st.label}>Description</label>
                <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} rows={2} style={{ ...st.input, resize: 'vertical' }} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Steward Email (invite will be sent)</label>
                <input value={formEmail} onChange={(e) => setFormEmail(e.target.value)} style={st.input} placeholder="steward@example.com" type="email" />
                <span style={{ color: COLORS.textMuted, fontSize: 10 }}>Invite sent from info@grimsthorpelake.com</span>
              </div>

              <div style={{ borderTop: `1px solid ${COLORS.borderDefault}`, marginTop: 12, paddingTop: 12 }}>
                <label style={{ ...st.label, marginBottom: 8, display: 'block' }}>Assign to Almanac Week (optional)</label>

                {/* Year */}
                <div style={st.field}>
                  <label style={{ ...st.label, fontSize: 10 }}>Year</label>
                  <select value={formYear} onChange={(e) => { setFormYear(parseInt(e.target.value, 10)); setFormWeek(0); setFormCampId(''); }} style={st.input}>
                    <option value={0}>-- Select Year --</option>
                    {almanacYears.map((y) => <option key={y.year} value={y.year}>{y.year} ({y.totalWeeks} weeks)</option>)}
                  </select>
                </div>

                {/* Week (only after year selected) */}
                {formYear > 0 && (() => {
                  const yearData = almanacYears.find((y) => y.year === formYear);
                  if (!yearData) return null;
                  const totalWeeks = yearData.totalWeeks || 52;
                  return (
                    <div style={st.field}>
                      <label style={{ ...st.label, fontSize: 10 }}>Almanac Week</label>
                      <select value={formWeek} onChange={(e) => { setFormWeek(parseInt(e.target.value, 10)); setFormCampId(''); }} style={st.input}>
                        <option value={0}>-- Select Week --</option>
                        {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((w) => {
                          const name = WEEK_NAMES[w] || `Week ${w}`;
                          const date = yearData.firstWeekStart ? getWeekDate(yearData.firstWeekStart, w) : '';
                          const isBonus = yearData.bonusWeek === w;
                          return (
                            <option key={w} value={w}>
                              W{w} — {name}{date ? ` (${date})` : ''}{isBonus ? ' [BONUS]' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })()}

                {/* Base Camp (only after week selected, filtered by open camps) */}
                {formYear > 0 && formWeek > 0 && (() => {
                  const yearData = almanacYears.find((y) => y.year === formYear);
                  if (!yearData) return null;
                  // Filter to camps that are open this week
                  const openCamps = camps.filter((camp) => {
                    const closure = closures.find((c) => c.campId === camp.id && c.almanacYearId === yearData.id);
                    const closedWeeks = closure ? (closure.weeks as number[]) : [];
                    return !closedWeeks.includes(formWeek);
                  });
                  const closedCamps = camps.filter((camp) => !openCamps.find((oc) => oc.id === camp.id));

                  return (
                    <div style={st.field}>
                      <label style={{ ...st.label, fontSize: 10 }}>Base Camp</label>
                      <select value={formCampId} onChange={(e) => setFormCampId(e.target.value)} style={st.input}>
                        <option value="">-- Select Base Camp --</option>
                        {openCamps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        {closedCamps.length > 0 && (
                          <optgroup label="Closed this week">
                            {closedCamps.map((c) => <option key={c.id} value={c.id} disabled>{c.name} (closed)</option>)}
                          </optgroup>
                        )}
                      </select>
                      {openCamps.length === 0 && (
                        <span style={{ color: '#FFC107', fontSize: 10 }}>All base camps are closed for week {formWeek}</span>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div style={st.panelActions}>
                <button style={st.btnSecondary} onClick={() => setPanel({ type: 'default' })}>Cancel</button>
                <button style={st.btnPrimary} onClick={createPack} disabled={!formName.trim() || !formEmail.trim()}>Create Pack</button>
              </div>
            </>
          )}

          {panel.type === 'created' && (
            <>
              <h3 style={st.panelTitle}>Pack Created!</h3>
              <div style={{ padding: 16, backgroundColor: 'rgba(40,167,69,0.1)', borderRadius: 8, marginBottom: 16 }}>
                <div style={{ color: '#28A745', fontWeight: 600, marginBottom: 8 }}>{panel.pack.name}</div>
                <div style={{ color: COLORS.textSecondary, fontSize: 12, marginBottom: 4 }}>
                  {panel.emailSent
                    ? <>Invite email sent to: <strong>{panel.email}</strong></>
                    : <>Invite for: <strong>{panel.email}</strong> <span style={{ color: '#FFC107', fontSize: 11 }}>(share code manually)</span></>
                  }
                </div>
                <div style={{ marginTop: 12, padding: 12, backgroundColor: COLORS.backgroundTertiary, borderRadius: 6, textAlign: 'center' }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>Invite Code</div>
                  <div style={{ color: COLORS.textGold, fontSize: 28, fontWeight: 700, letterSpacing: 4, fontFamily: 'monospace' }}>{panel.code}</div>
                </div>
                <p style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 12 }}>
                  Share this code with the steward. They enter it in the Grimsthorpe Lake app after signing in. Code expires in 30 days.
                </p>
              </div>
              <button style={{ ...st.btnPrimary, width: '100%' }} onClick={() => setPanel({ type: 'default' })}>Done</button>
            </>
          )}

          {panel.type === 'view' && (
            <>
              <h3 style={st.panelTitle}>{panel.pack.name}</h3>
              {panel.pack.description && <p style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 12 }}>{panel.pack.description}</p>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                <DuesBadge status={panel.pack.duesStatus} />
                <span style={{ ...st.badge, backgroundColor: 'rgba(255,255,255,0.05)', color: COLORS.textMuted }}>
                  {panel.pack.memberCount} member{panel.pack.memberCount !== 1 ? 's' : ''}
                </span>
              </div>

              <div style={st.field}>
                <label style={st.label}>Steward</label>
                <div style={{ color: COLORS.textPrimary, fontSize: 13 }}>
                  {panel.pack.captain ? `${panel.pack.captain.displayName} (${panel.pack.captain.email})` : <em style={{ color: COLORS.textMuted }}>Not yet assigned — pending invite</em>}
                </div>
              </div>

              {panel.pack.pendingInvites?.length > 0 && (
                <div style={st.field}>
                  <label style={st.label}>Pending Invites</label>
                  {panel.pack.pendingInvites.map((inv) => (
                    <div key={inv.id} style={{ padding: '6px 8px', borderRadius: 4, border: `1px solid ${COLORS.borderDefault}`, marginBottom: 4 }}>
                      <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>{inv.inviteeEmail || 'No email'}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: 10 }}>
                        Code: <strong style={{ color: COLORS.textGold, fontFamily: 'monospace' }}>{inv.code}</strong>
                        {' · '}{inv.role} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={st.panelActions}>
                <button style={st.btnSecondary} onClick={() => setPanel({ type: 'default' })}>Close</button>
                <button style={st.btnSmall} onClick={() => sendInvite(panel.pack.id)}>+ Invite</button>
                <button style={st.btnPrimary} onClick={() => openEdit(panel.pack)}>Edit</button>
              </div>
            </>
          )}

          {panel.type === 'edit' && (
            <>
              <h3 style={st.panelTitle}>Edit: {panel.pack.name}</h3>
              <div style={st.field}>
                <label style={st.label}>Name</label>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} style={st.input} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...st.field, flex: 1 }}>
                  <label style={st.label}>Dues Status</label>
                  <select value={editDuesStatus} onChange={(e) => setEditDuesStatus(e.target.value)} style={st.input}>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="overdue">Overdue</option>
                    <option value="grace">Grace</option>
                  </select>
                </div>
                <div style={{ ...st.field, flex: 1 }}>
                  <label style={st.label}>Amount ($)</label>
                  <input type="number" step="0.01" value={editDuesAmount} onChange={(e) => setEditDuesAmount(e.target.value)} style={st.input} />
                </div>
              </div>
              <div style={st.field}>
                <label style={st.label}>Dues Notes</label>
                <textarea value={editDuesNotes} onChange={(e) => setEditDuesNotes(e.target.value)} rows={2} style={{ ...st.input, resize: 'vertical' }} />
              </div>
              <div style={st.field}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: COLORS.textMuted, fontSize: 12 }}>
                  <input type="checkbox" checked={editArchived} onChange={(e) => setEditArchived(e.target.checked)} />
                  Archived
                </label>
              </div>
              <div style={st.panelActions}>
                <button style={{ ...st.btnDanger, marginRight: 'auto' }} onClick={() => deletePack(panel.pack)}>Delete</button>
                <button style={st.btnSecondary} onClick={() => setPanel({ type: 'default' })}>Cancel</button>
                <button style={st.btnPrimary} onClick={savePack}>Update</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const st: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  leftCol: { flex: 1, overflowY: 'auto', padding: '0 20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingTop: 12 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  pageSubtitle: { fontSize: 13, color: COLORS.textMuted, marginTop: 4 },
  card: { backgroundColor: COLORS.backgroundSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 12, padding: 16 },
  panelCol: { width: 380, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  empty: { color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' },
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: COLORS.textMuted },
  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnSmall: { padding: '4px 10px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' },
  btnSmallMuted: { padding: '4px 10px', backgroundColor: 'transparent', color: COLORS.textMuted, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 4, fontSize: 11, cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
};
